import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaStar,
  FaQuoteLeft,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaPause,
  FaPlay
} from 'react-icons/fa';
import './Testimonials.css';
import { Helmet } from 'react-helmet';

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  const testimonials = [
    {
      name: 'Rahul Verma',
      role: 'Competitive Player',
      rating: 5,
      content: 'Imperial Badminton Academy transformed my game completely. The coaches here are incredibly knowledgeable and supportive. I went from a district level player to representing my state in just two years! The personalized attention and scientific training methods made all the difference.',
    },
    {
      name: 'Ankit Sharma',
      role: 'State-Level Player',
      rating: 5,
      content: 'The structured training system here is outstanding. Every session is well planned, and the coaches give individual feedback that really helps improve weaknesses. Imperial Badminton Academy is the best place for serious badminton players.',
    },
    {
      name: 'Neha Gupta',
      role: 'Parent',
      rating: 5,
      content: 'My child has shown tremendous improvement in both skills and discipline since joining Imperial Badminton Academy. The coaches are supportive, professional, and very patient with young players. I highly recommend this academy to parents.',
    },
    {
      name: 'Rohan Mehta',
      role: 'Intermediate Player',
      rating: 5,
      content: 'I joined as an intermediate player and saw major improvement in my footwork and shot accuracy. Match practice sessions helped me understand real game situations better. The academy truly focuses on overall player development.',
    },
    {
      name: 'Pooja Singh',
      role: 'Beginner Player',
      rating: 5,
      content: 'As a beginner, I felt very comfortable here. The coaches explain everything clearly and encourage learning step by step. The environment is friendly and positive, making training enjoyable and motivating.',
    },
    {
      name: 'Amit Khanna',
      role: 'Tournament Player',
      rating: 5,
      content: 'Imperial Badminton Academy helped me prepare for competitive tournaments with advanced drills and match simulations. The fitness training and mental conditioning sessions made a big difference in my performance.',
    },
    {
      name: 'Suresh Kumar',
      role: 'Parent',
      rating: 5,
      content: 'The academy maintains excellent discipline and safety standards. My son enjoys training here and has become more focused and confident. The coaching quality and infrastructure are truly impressive.',
    },
    {
      name: 'Kavita Malhotra',
      role: 'Working Professional',
      rating: 5,
      content: 'I joined the academy for fitness and recreational badminton. The training sessions are well balanced and help improve stamina and skills. It’s a great place for adults who want to stay fit and enjoy badminton.',
    },
    {
      name: 'Arjun Patel',
      role: 'Junior Player',
      rating: 5,
      content: 'The coaches motivate us to work hard and improve every day. I learned proper technique, discipline, and match strategies here. Imperial Badminton Academy has helped me grow as a player and as a person.',
    },
    {
      name: 'Deepak Yadav',
      role: 'Advanced Player',
      rating: 5,
      content: 'This academy focuses on long-term development, not just winning matches. The detailed training plans and regular feedback sessions helped me become more consistent and confident in competitive play.',
    },
    {
      name: 'Sunita Verma',
      role: 'Parent',
      rating: 5,
      content: 'What I appreciate most is the personal attention given to each player. The coaches regularly update parents about progress and areas of improvement. Imperial Badminton Academy truly cares about player growth.',
    },
    {
      name: 'Mohit Arora',
      role: 'Club-Level Player',
      rating: 5,
      content: 'The facilities and training environment are excellent. Indoor courts, proper lighting, and advanced equipment make training comfortable and professional. This academy sets high standards in badminton coaching.',
    },
    {
      name: 'Riya Chawla',
      role: 'Youth Player',
      rating: 5,
      content: 'Training here has boosted my confidence and improved my fitness. The coaches encourage discipline, hard work, and positivity. I enjoy every training session and feel motivated to improve further.',
    },
    {
      name: 'Sanjay Bansal',
      role: 'Parent',
      rating: 5,
      content: 'Imperial Badminton Academy provides a perfect balance of skill training and character development. My child has learned teamwork, discipline, and sportsmanship along with badminton skills.',
    },
    {
      name: 'Vikram Singh',
      role: 'Amateur Player',
      rating: 5,
      content: 'I have trained at several academies, but Imperial Badminton Academy stands out for its professionalism and structured coaching. The coaches genuinely care about improvement and long-term success.',
    },
    {
      name: 'Aditi Kapoor',
      role: 'Competitive Player',
      rating: 5,
      content: 'The academy has played a major role in shaping my badminton journey. From technical improvement to mental strength, everything is covered in training. I feel more prepared and confident in competitions now.',
    },
  ];


  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Get visible testimonials for the slider (current, prev, next)
  const getVisibleTestimonials = () => {
    const prev = (currentSlide - 1 + testimonials.length) % testimonials.length;
    const next = (currentSlide + 1) % testimonials.length;
    return { prev, current: currentSlide, next };
  };

  const visible = getVisibleTestimonials();

  return (
    <>
      <Helmet>
        <title>Testimonials</title>
      </Helmet>
      <div className="testimonials-page">
        <div className="page-header">
          <div className="container">
            <h1>Testimonials</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Testimonials</span>
            </div>
          </div>
        </div>

        <section className="testimonials-intro section">
          <div className="container">
            <div className="section-title">
              <h2>Stories of Success</h2>
              <p>
                Don't just take our word for it. Here's what our students, parents,
                and members have to say about their experience at Imperial Badminton Academy.
              </p>
            </div>
          </div>
        </section>

        {/* Main Testimonial Slider */}
        <section className="testimonials-slider-section section bg-light">
          <div className="container">
            <div className="slider-wrapper">
              {/* Navigation Buttons */}
              <button
                className="slider-btn slider-btn-prev"
                onClick={prevSlide}
                aria-label="Previous testimonial"
              >
                <FaChevronLeft />
              </button>

              <div className="testimonial-slider">
                <div className="slider-track">
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={index}
                      className={`slider-card ${index === visible.current
                        ? 'active'
                        : index === visible.prev
                          ? 'prev'
                          : index === visible.next
                            ? 'next'
                            : 'hidden'
                        }`}
                    >
                      <div className="slider-card-inner">
                        <div className="quote-icon-large">
                          <FaQuoteLeft />
                        </div>
                        <div className="slider-content">
                          <p className="testimonial-text">{testimonial.content}</p>
                          <div className="testimonial-rating">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                        </div>
                        <div className="slider-author">
                          <div className="author-info">
                            <h4>{testimonial.name}</h4>
                            <p className="author-role">{testimonial.role}</p>
                            <p className="author-duration">{testimonial.years}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="slider-btn slider-btn-next"
                onClick={nextSlide}
                aria-label="Next testimonial"
              >
                <FaChevronRight />
              </button>
            </div>

            {/* Slider Controls */}
            <div className="slider-controls">
              {/* Dots */}
              <div className="slider-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              {/* Play/Pause Button */}
              <button
                className="autoplay-btn"
                onClick={toggleAutoPlay}
                aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
              >
                {isAutoPlaying ? <FaPause /> : <FaPlay />}
                <span>{isAutoPlaying ? 'Pause' : 'Play'}</span>
              </button>
            </div>

            {/* Progress Bar */}
            <div className="slider-progress">
              <div
                className="progress-bar"
                style={{
                  width: `${((currentSlide + 1) / testimonials.length) * 100}%`
                }}
              />
            </div>

            {/* Slide Counter */}
            <div className="slide-counter">
              <span className="current">{String(currentSlide + 1).padStart(2, '0')}</span>
              <span className="separator">/</span>
              <span className="total">{String(testimonials.length).padStart(2, '0')}</span>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="testimonials-cta section">
          <div className="container">
            <div className="cta-box">
              <h2>Ready to Write Your Success Story?</h2>
              <p>
                Join Imperial Badminton Academy and start your journey towards
                becoming the player you've always dreamed of being.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent">
                  Join Now <FaArrowRight />
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  View Programs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Testimonials;
